'use client';
// ct2 chrome for every NON-category public page (Founder 15/10/26). Same Ct2Chrome as the category pages — only the dock items differ.
// Mounted by SiteShell (pro, home, hubs, search, utility pages) and by ArticleView (articles). ?v=old = the previous chrome (escape hatch).
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Ct2Chrome, { type Ct2Item, type Ct2Site as Site } from './Ct2Chrome';
import { CT2_CSS } from './ct2Css';
import type { Ct2Cat } from '@/lib/ct2Menu';

export type Ct2Kind = 'pro' | 'article' | 'home' | 'generic';
/** /{cat}/{slug}-{digits} is a pro page OR an article whose slug ends in digits (…-מחיר-2): only the server page knows, so it mounts its own Ct2Site (20/10/26: SiteShell mounting a pro one next to the article's own = double header). */
export const ct2PageMountsOwn = (p: string | null | undefined) => /^\/(?:[^/]+\/[^/]+-\d+|\d+)$/.test(norm(p || '/'));
const CONSULT_ITEM_NONE: Ct2Item[] = [];
const norm = (p: string) => { try { return decodeURIComponent(p).replace(/\/+$/, '') || '/'; } catch { return p; } };

export function ct2KindForPath(path: string | null | undefined): Ct2Kind {
  const p = norm(path || '/');
  if (p === '/') return 'home';
  if (/^\/\d+$/.test(p) || /^\/[^/]+\/[^/]+-\d+$/.test(p)) return 'pro';
  return 'generic';
}

export default function Ct2Site({ kind, cats, consultCat, currentCat, noDock = false }: { kind: Ct2Kind; cats: Ct2Cat[]; consultCat?: string | null; currentCat?: string | null; noDock?: boolean }) {
  const path = usePathname();
  const [off, setOff] = useState(false);
  useEffect(() => { try { if (new URLSearchParams(location.search).get('v') === 'old') setOff(true); } catch {} }, []);
  if (off) return null;
  const p = norm(path || '/');
  let items: Ct2Item[] = CONSULT_ITEM_NONE;
  if (kind === 'pro') items = [{ key: 'top', label: 'המומחה', icon: 'user', id: 'ct2-pro-top' }, { key: 'rev', label: 'המלצות', icon: 'star', id: 'ct2-reviews' }, { key: 'gal', label: 'גלריה', icon: 'photo', id: 'ct2-gallery' }];
  else if (kind === 'article') items = [{ key: 'top', label: 'המאמר', icon: 'doc', id: 'ct2-art-top' }, { key: 'faq', label: 'שאלות', icon: 'faq', id: 'ct2-faq' }, { key: 'pros', label: 'מומחים', icon: 'user', id: 'ct2-art-pros' }];
  else if (kind === 'home') items = [{ key: 'exp', label: 'המומחים', icon: 'user', id: 'ct2-home-experts' }, { key: 'art', label: 'מדריכים', icon: 'doc', id: 'ct2-home-articles' }];
  else items = ([{ key: 'cats', label: 'קטגוריות', icon: 'grid', href: '/קטגוריה' }, { key: 'art', label: 'מדריכים', icon: 'doc', href: '/מאמרים' }] as Ct2Item[])
    .filter(i => norm(i.href!) !== p && !(i.key === 'cats' && p === '/category') && !(i.key === 'art' && p === '/articles'));
  const site: Site = { noDock, mobNoDock: kind === 'pro', deskNav: !noDock && (kind === 'pro' || kind === 'article'), items, consultCat: consultCat ?? (kind === 'pro' ? (/^\/(\d+)$/.test(p) ? null : decodeURIComponent(p.split('/')[1] || '')) : null), currentCat: currentCat ?? (kind === 'pro' ? decodeURIComponent(p.split('/')[1] || '') : null) };
  return (
    <div className={`ct2-root ct2-site${kind === 'pro' ? ' ct2-kind-pro' : ''}${kind === 'home' ? ' ct2-kind-home' : ''}${kind === 'article' && !noDock ? ' ct2-deskhdr' : ''}`} style={{ display: 'contents' }}>
      <style>{CT2_CSS}</style>
      <Ct2Chrome cats={cats} site={site} />
    </div>
  );
}
